<script setup>
import { ref, computed, onMounted } from "vue";
import { useTaskStore } from "@/stores/taskStore";
import { useProjectStore } from "@/stores/projectStore";
import { useBoardStore } from "@/stores/boardStore";
import TaskBoardKanban from "./task-board-kanban.vue";
import TasksBoardModal from "@/components/modal/tasks-board-modal.vue";
import TasksModal from "@/components/modal/tasks-modal.vue";
import TasksViewModal from "@/components/modal/tasks-view-modal.vue";
import { Modal } from "bootstrap";
import { useAuthStore } from "@/stores/authStore";

const title = "Task Board",
  text = "Projects",
  text1 = "Task Board";

const auth = useAuthStore();
const taskStore = useTaskStore();
const projectStore = useProjectStore();
const boardStore = useBoardStore();

const isEmployee = computed(() => auth.roles?.includes("Employee"));

const q = ref("");
const selectedStatus = ref("");
const selectedPriorityTab = ref("All");
const sort = ref("created");
const projectId = ref("");
const startdate = ref("");
const startdateOne = ref("");
const exporting = ref("");

const editRef = ref(null);
const viewRef = ref(null);
const modalRef = ref(null);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

const projectOptions = computed(() =>
  (projectStore.items || []).map((p) => ({ id: p.id, label: p.name }))
);

let t;
function debouncedReload() {
  clearTimeout(t);
  t = setTimeout(() => reload(), 250);
}

// function normalizeTask(t) {
//   const assignees = Array.isArray(t.assignees) ? t.assignees : [];
//   const avatars = t.assignee_avatars?.length
//     ? t.assignee_avatars
//     : assignees.map((a) => a.avatar_url || a.avatar || null);
//   return {
//     id: t.id,
//     title: t.title || "—",
//     status: t.status || "Pending",
//     priority: t.priority || "Medium",
//     due_pretty: t.due_pretty || null,
//     progress: t.progress || 0,
//     assignee_avatars: avatars,
//   };
// }

function normalizeTask(t) {
  const assignees = Array.isArray(t.assignees) ? t.assignees : [];
  const avatars = t.assignee_avatars?.length
    ? t.assignee_avatars
    : assignees.map((a) => a.avatar_url || a.avatar || null);

  return {
    id: t.id,
    title: t.title || "—",
    status: t.status || "Pending",
    priority: t.priority || "Medium",
    progress: t.progress ?? 0, // if you don’t have it, default 0 or undefined
    due_pretty: t.due_pretty || null, // or compute from t.due_date
    assignee_avatars: avatars,
    comments: t.comments ?? null,
    attachments: t.attachments ?? null,
    typeLabel: t.typeLabel || null,
  };
}

const loading = computed(() => taskStore.loading);
const columns = computed(() => {
  const rows = (taskStore.items || []).map(normalizeTask);
  const byPriority =
    selectedPriorityTab.value === "All"
      ? rows
      : rows.filter((r) => r.priority === selectedPriorityTab.value);
  const filtered = selectedStatus.value
    ? byPriority.filter((r) => r.status === selectedStatus.value)
    : byPriority;

  const buckets = {
    Pending: [],
    Inprogress: [],
    Completed: [],
    Onhold: [],
  };
  for (const r of filtered) (buckets[r.status] || buckets.Pending).push(r);

  const sorter =
    sort.value === "priority"
      ? (a, b) =>
          ({ High: 0, Medium: 1, Low: 2 }[a.priority] -
          { High: 0, Medium: 1, Low: 2 }[b.priority])
      : sort.value === "due"
      ? (a, b) => (a.due_pretty || "").localeCompare(b.due_pretty || "")
      : () => 0;

  Object.keys(buckets).forEach((k) => buckets[k].sort(sorter));

  return [
    {
      key: "Pending",
      dotBg: "bg-soft-pink",
      dot: "bg-pink",
      title: "Pending",
      items: buckets.Pending,
    },
    {
      key: "Inprogress",
      dotBg: "bg-soft-skyblue",
      dot: "bg-skyblue",
      title: "Inprogress",
      items: buckets.Inprogress,
    },
    {
      key: "Completed",
      dotBg: "bg-soft-success",
      dot: "bg-success",
      title: "Completed",
      items: buckets.Completed,
    },
    {
      key: "Onhold",
      dotBg: "bg-soft-warning",
      dot: "bg-warning",
      title: "On-hold",
      items: buckets.Onhold,
    },
  ];
});

const totalCount = computed(() => (taskStore.items || []).length);
const pendingCount = computed(
  () =>
    (taskStore.items || []).filter((x) => (x.status || "Pending") === "Pending")
      .length
);
const completedCount = computed(
  () =>
    (taskStore.items || []).filter(
      (x) => (x.status || "Pending") === "Completed"
    ).length
);

async function handleMove({ taskId, toStatus }) {
  try {
    await taskStore.update(taskId, { status: toStatus });
    await reload();
  } catch {}
}
function handleView(id) {
  viewRef.value?.open(id);
}
function handleEdit(id) {
  modalRef.value?.openEdit(id);
}

// async function handleDelete(id) {
//   if (!confirm("Delete this task?")) return;
//   await taskStore.remove(id);
//   await reload();
// }

async function handleDelete(id) {
  // 1) Tell the modal which record we plan to delete
  modalRef.value?.openForDelete(id);

  // 2) Show the delete confirmation modal
  const el = document.getElementById("delete_task");
  if (el) {
    new Modal(el, { backdrop: "static", keyboard: false }).show();
  }
}
function handleAdd(columnKey) {
  // new task with default status + current board
  modalRef.value?.openCreate({
    status: columnKey,
    board_id: boardStore.current?.id || null,
  });
}

function buildParams() {
  return {
    q: q.value || undefined,
    project_id: projectId.value || undefined,
    status: selectedStatus.value || undefined,
    priority:
      selectedPriorityTab.value !== "All"
        ? selectedPriorityTab.value
        : undefined,
    sort: sort.value,
    due_from: startdate.value || undefined,
    due_to: startdateOne.value || undefined,
    board_id: boardStore.current?.id || undefined,
    scope: isEmployee.value ? "me" : undefined,
  };
}
function syncTaskFilters() {
  const params = buildParams();
  taskStore.filters.q = params.q || "";
  taskStore.filters.project_id = params.project_id || "";
  taskStore.filters.status = params.status || "";
  taskStore.filters.priority = params.priority || "";
  taskStore.filters.sort = params.sort || "created";
  taskStore.filters.due_from = params.due_from || "";
  taskStore.filters.due_to = params.due_to || "";
  taskStore.filters.scope = params.scope || "";
  return params;
}
async function reload() {
  const params = syncTaskFilters();
  await taskStore.list(params);
}

async function exportBoard(format = "xlsx") {
  try {
    exporting.value = format;
    const params = syncTaskFilters();
    await taskStore.downloadReport(format, params);
  } finally {
    exporting.value = "";
  }
}

// async function reload() {
//   const params = buildParams();
//   Object.assign(taskStore.filters, params); // persist
//   await taskStore.list(params);
// }

function selectBoard(b) {
  boardStore.setCurrent(b);
  reload();
}

onMounted(async () => {
  if (!projectStore.items?.length) await projectStore.list();
  await boardStore.list(); // sets current
  await reload();
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap"
        >
          <div class="dropdown me-2">
            <a
              href="javascript:void(0);"
              class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
              data-bs-toggle="dropdown"
            >
              <i class="ti ti-file-export me-2"></i> Export
            </a>
            <ul class="dropdown-menu dropdown-menu-end p-3">
              <li>
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click.prevent="exportBoard('pdf')"
                >
                  <span
                    v-if="exporting === 'pdf'"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  <i v-else class="ti ti-file-type-pdf me-1"></i>Export as PDF
                </a>
              </li>
              <li>
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click.prevent="exportBoard('xlsx')"
                >
                  <span
                    v-if="exporting === 'xlsx'"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  <i v-else class="ti ti-file-type-xls me-1"></i>Export as Excel
                </a>
              </li>
            </ul>
          </div>
          <a
            href="javascript:void(0);"
            class="btn btn-primary d-inline-flex align-items-center"
            data-bs-toggle="modal"
            data-bs-target="#add_board"
            v-can="'TaskBoard.Create'"
          >
            <i class="ti ti-circle-plus me-1"></i>Add Board
          </a>
          <div class="head-icons ms-2 mb-0">
            <a
              href="javascript:void(0);"
              id="collapse-header"
              @click="toggleHeader"
              ><i class="ti ti-chevrons-up"></i
            ></a>
          </div>
        </div>
      </div>

      <div
        class="card"
        :class="
          boardStore.current
            ? `border-top border-3 border-${boardStore.current.color}`
            : ''
        "
      >
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <!-- Dynamic board title + switcher -->
          <div class="d-flex align-items-center gap-2">
            <h4 class="mb-0">{{ boardStore.current?.name || "All Boards" }}</h4>
            <div class="dropdown">
              <a
                class="btn btn-sm btn-light dropdown-toggle"
                href="javascript:void(0);"
                data-bs-toggle="dropdown"
              >
                Switch Board
              </a>
              <ul class="dropdown-menu">
                <li>
                  <a
                    class="dropdown-item"
                    href="javascript:void(0);"
                    @click="
                      () => {
                        boardStore.setCurrent(null);
                        reload();
                      }
                    "
                  >
                    <span class="me-2 badge">&nbsp;</span> All Boards
                  </a>
                </li>
                <li v-for="b in boardStore.items" :key="b.id">
                  <a
                    class="dropdown-item"
                    href="javascript:void(0);"
                    @click="selectBoard(b)"
                  >
                    <span
                      class="me-2"
                      :class="`badge bg-${b.color || 'primary'}`"
                      >&nbsp;</span
                    >
                    {{ b.name }}
                  </a>
                </li>
                <li v-if="!boardStore.items.length">
                  <span class="dropdown-item text-muted">No boards</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="d-flex align-items-center flex-wrap row-gap-3">
            <!-- <div class="avatar-list-stacked avatar-group-sm me-3">
              <span class="avatar avatar-rounded"
                ><img
                  class="border border-white"
                  src="@/assets/img/profiles/avatar-19.jpg"
                  alt="img"
              /></span>
              <span class="avatar avatar-rounded"
                ><img
                  class="border border-white"
                  src="@/assets/img/profiles/avatar-29.jpg"
                  alt="img"
              /></span>
              <span class="avatar avatar-rounded"
                ><img
                  class="border border-white"
                  src="@/assets/img/profiles/avatar-16.jpg"
                  alt="img"
              /></span>
              <span class="avatar avatar-rounded bg-primary fs-12"> 1+ </span>
            </div> -->
            <div class="d-flex align-items-center me-3">
              <p class="mb-0 me-3 pe-3 border-end fs-14">
                Total Task : <span class="text-dark">{{ totalCount }}</span>
              </p>
              <p class="mb-0 me-3 pe-3 border-end fs-14">
                Pending : <span class="text-dark">{{ pendingCount }}</span>
              </p>
              <p class="mb-0 fs-14">
                Completed : <span class="text-dark">{{ completedCount }}</span>
              </p>
            </div>
            <div class="input-icon-start position-relative me-2">
              <span class="input-icon-addon"><i class="ti ti-search"></i></span>
              <input
                v-model="q"
                @input="debouncedReload"
                type="text"
                class="form-control"
                placeholder="Search Project / Task"
              />
            </div>
          </div>
        </div>

        <div class="card-body">
          <task-board-kanban
            :columns="columns"
            :loading="loading"
            @moved="handleMove"
            @view="handleView"
            @edit="handleEdit"
            @delete="handleDelete"
            @add="handleAdd"
          />
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>

  <!-- Modals -->
  <tasks-board-modal />
  <tasks-modal ref="modalRef" @saved="reload" />
  <tasks-view-modal ref="viewRef" />
</template>

<style scoped>
/* Example palette — change to your brand colors */
.border-brightblue {
  border-color: #3b82f6 !important;
} /* Tailwind blue-500 */
.border-lunargreen {
  border-color: #16a34a !important;
} /* green-600 */
.border-lavendar {
  border-color: #a78bfa !important;
} /* violet-400 */
.border-magenta {
  border-color: #d946ef !important;
} /* fuchsia-500 */
.border-chromeyellow {
  border-color: #f59e0b !important;
} /* amber-500 */
.border-lavared {
  border-color: #ef4444 !important;
} /* red-500 */
.border-primary {
  border-color: var(--bs-primary) !important;
} /* keep bootstrap too */

/* If you also use bg-${color} badges in the UI: */
.bg-brightblue {
  background-color: #3b82f6 !important;
  color: #fff !important;
}
.bg-lunargreen {
  background-color: #16a34a !important;
  color: #fff !important;
}
.bg-lavendar {
  background-color: #a78bfa !important;
  color: #fff !important;
}
.bg-magenta {
  background-color: #d946ef !important;
  color: #fff !important;
}
.bg-chromeyellow {
  background-color: #f59e0b !important;
  color: #111 !important;
}
.bg-lavared {
  background-color: #ef4444 !important;
  color: #fff !important;
}
</style>


